import React, { useEffect, useState } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import CreateBankAccount from "./CreateBankAccount"
import SignContract from "./SignContract"
import { useDispatch, useStore } from "react-redux"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { isSignedMainDoc } from "../../features/documents/documents"
import { getBankAccountAnonymous } from "../../features/payment/paymentSlice"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { SIGNING_THANK_YOU_TEXT } from "../../config/featureFlagKeys"
import { signingSuccessText } from "./signingSuccessText"
import ExpiredLink, { EXPIRED_LINK_MESSAGE } from "./ExpiredLink"
import { LINK_TOKEN_REFUSAL } from "../../utils/linkToken"

const BankAccount = () => {
  const navigate = useNavigate()
  let { userId } = useParams()
  const dispatch = useDispatch()
  const store = useStore()
  const { triggerLoading } = useMyContextLoading()
  const location = useLocation()

  const [isEnrollment] = useState(() => {
    if (location.state?.source === 'enrollment') {
      localStorage.setItem(`enrollment_source_${userId}`, 'true')
      return true
    }
    return localStorage.getItem(`enrollment_source_${userId}`) === 'true'
  })

  const [currentStep, setCurrentStep] = useState(1)
  const [linkRefusal, setLinkRefusal] = useState(null)


  const handleNextStep = () => {
    setCurrentStep(currentStep + 1)
  }

  const checkIsFlowEnded = async () => {
    triggerLoading(true)
    try {
      const maxAttempts = 3
      const retryDelayMs = 1500
      const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

      const resolveThunk = async (createThunk, isFulfilled) => {
        let action
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          action = await dispatch(createThunk())
          if (isFulfilled(action)) return action
          if (action?.payload?.code === LINK_TOKEN_REFUSAL) return action
          if (attempt < maxAttempts) await wait(retryDelayMs)
        }
        return action
      }

      const signedAction = await resolveThunk(() => isSignedMainDoc({ userId }), isSignedMainDoc.fulfilled.match)
      const isMainDocSigned = isSignedMainDoc.fulfilled.match(signedAction) ? signedAction?.payload?.data : false
      const firmBrand = isMainDocSigned ? signedAction?.payload?.firmBrand : null

      const bankAction = await resolveThunk(() => getBankAccountAnonymous({ userId }), getBankAccountAnonymous.fulfilled.match)
      const bankAccount = getBankAccountAnonymous.fulfilled.match(bankAction) ? bankAction?.payload?.data : undefined

      if (isMainDocSigned) {
        localStorage.removeItem(`enrollment_source_${userId}`)
        const thankYouTextFlag = selectFeatureFlag(SIGNING_THANK_YOU_TEXT)(store.getState())
        navigate("/success/signing", {
          replace: true,
          state: {
            text: isEnrollment
              ? { heading: "Welcome to the first step toward becoming debt-free.", subtext: "Someone from our team will reach out to you shortly." }
              : signingSuccessText(thankYouTextFlag),
            ...(firmBrand ? { firmBrand } : {}),
          }
        })
      } else if (bankAction?.payload?.code === LINK_TOKEN_REFUSAL) {
        setLinkRefusal(bankAction.payload.message || EXPIRED_LINK_MESSAGE)
      } else if (bankAccount) {
        handleNextStep()
      }
    } catch (error) {
      console.error("Error checking bank account flow:", error)
    }
    triggerLoading(false)
  }
  useEffect(() => {
    checkIsFlowEnded()
  }, [])

  if (linkRefusal) {
    return <ExpiredLink message={linkRefusal} testId="signing-link-expired" />
  }

  return (
    <div>
      {currentStep === 1 && <CreateBankAccount onNextStep={handleNextStep} />}
      {currentStep === 2 && (
        <SignContract
          userId={userId}
          isEnrollment={isEnrollment}
          onLinkRefused={(message) => setLinkRefusal(message || EXPIRED_LINK_MESSAGE)}
        />
      )}
    </div>
  )
}

export default BankAccount
